import Bluebird from "bluebird";
import ExponentialIntervalGenerator from "util/ExponentialIntervalGenerator";
import errorCallbackFactory from "util/errorCallbackFactory";

interface RetryOptions {
    maxRetries: number;
    startIntervalMs: number;
    maxIntervalMs: number;
}

enum MaxRetryBounds {
    DEFAULT = 1,
    MAX = 10,
}

const defaultRetryOptions: Readonly<RetryOptions> = {
    maxRetries: MaxRetryBounds.DEFAULT,
    startIntervalMs: 0,
    maxIntervalMs: 60 * 1000,
};

export class MaxHttpRequestRetriesExceededError extends Error {
    constructor(numRetries: number) {
        super(
            `Maximum retries exceeded for request.  numRetries: ${numRetries}`
        );
    }
}

function scheduleRetry(
    numRetries: number,
    intervalManager: ExponentialIntervalGenerator,
    { maxRetries }: RetryOptions,
    doRetry: () => Promise<any>,
    doRetrySucceeded: () => any,
    doMaxRetriesExceeded: (err: MaxHttpRequestRetriesExceededError) => void
) {
    const timeout = intervalManager.getInterval();

    if (numRetries >= maxRetries) {
        doMaxRetriesExceeded(
            new MaxHttpRequestRetriesExceededError(numRetries)
        );
        return undefined;
    }

    // prepare for next invocation
    intervalManager.fail();

    return window.setTimeout(() => {
        doRetry().then(doRetrySucceeded);
    }, timeout);
}

type RequestFn<A, R> = (...args: A[]) => Promise<R>;
type MatcherFn<B> = (arg: B) => boolean;

/**
 * @param  {() => Promise<R>} request - a function returning a promise that may reject with an HttpError
 * @param  {() => boolean} thenRetryMatcherFn - function called with promise result, return true if request should be retried. By default, will never retry successes.
 * @param  {() => boolean} catchRetryMatcherFn - function called with promise rejection, return true if request should be retried. By default, will always retry rejections.
 * @param  {Partial<RetryOptions>=retryOptionsDefaults} retryOptions
 * @throws {MaxHttpRequestRetriesExceededError}
 * @throws {Error}
 */
export function withRequestRetry<A, R>(
    request: RequestFn<A, R>,
    thenRetryMatcherFn: MatcherFn<R | undefined> = () => {
        return false;
    },
    catchRetryMatcherFn: MatcherFn<Error | undefined> = () => {
        return true;
    },
    retryOptions: Partial<RetryOptions> = defaultRetryOptions
): RequestFn<A, R> {
    if (!request) {
        throw new Error(
            "Failed to provide a request function for which to retry"
        );
    }
    if (
        typeof retryOptions.maxRetries === "number" &&
        retryOptions.maxRetries < MaxRetryBounds.DEFAULT
    ) {
        throw new Error(
            `Specified max retries of ${retryOptions.maxRetries} must be greater than or equal to lower bound of ${MaxRetryBounds.DEFAULT}`
        );
    }
    if (
        retryOptions.maxRetries &&
        retryOptions.maxRetries > MaxRetryBounds.MAX
    ) {
        throw new Error(
            `Specified max retries of ${retryOptions.maxRetries} exceeds upper bound of ${MaxRetryBounds.MAX}`
        );
    }

    const mergedRetryOptions: RetryOptions = {
        ...defaultRetryOptions,
        ...retryOptions, // overrides
    };

    const { maxIntervalMs, startIntervalMs } = mergedRetryOptions;
    let resolver: (thenableOrResult?: R | PromiseLike<R> | undefined) => void;
    let rejector: (error?: Error) => void;
    let onCancel: ((callback: () => void) => void) | undefined;
    const promise = new Bluebird<R>((resolve, reject, cancelCallback) => {
        resolver = resolve;
        rejector = reject;
        onCancel = cancelCallback;
    });

    return (...requestArgs) => {
        let retryPid: number | undefined;

        if (onCancel) {
            onCancel(() => {
                clearTimeout(retryPid);
            });
        }

        const doRetryableRequest = function doRetryableRequest(
            numRetries = 0,
            intervalManager = new ExponentialIntervalGenerator(
                maxIntervalMs,
                startIntervalMs
            )
        ) {
            const doScheduleRetry = () => {
                return scheduleRetry(
                    numRetries,
                    intervalManager,
                    mergedRetryOptions,
                    () => doRetryableRequest(numRetries + 1, intervalManager),
                    resolver,
                    rejector
                );
            };

            request(...requestArgs)
                .then((promiseResult?: R) => {
                    if (thenRetryMatcherFn(promiseResult)) {
                        retryPid = doScheduleRetry();
                    } else {
                        resolver(promiseResult);
                    }
                })
                .catch((error?: Error) => {
                    if (catchRetryMatcherFn(error)) {
                        retryPid = doScheduleRetry();
                    } else {
                        rejector(error);
                    }
                });

            return promise;
        };

        return doRetryableRequest();
    };
}

/**
 * @param  {() => Promise<R>} request - a function returning a promise that may reject with an HttpError
 * @param  {number[]} httpErrorCodes - http error codes to retry
 * @param  {Partial<RetryOptions>=retryOptionsDefaults} retryOptions
 * @throws {MaxHttpRequestRetriesExceededError}
 * @throws {Error}
 */
export function withHttpErrorRetry<A, R>(
    request: RequestFn<A, R>,
    httpErrorCodes: number[],
    retryOptions: Partial<RetryOptions> = defaultRetryOptions
): RequestFn<A, R> {
    if (!httpErrorCodes || !httpErrorCodes.length) {
        throw new Error(
            "Failed to provide HTTP response codes for which to retry"
        );
    }

    const strategies = httpErrorCodes.reduce(
        (strategies, httpErrorCode) => ({
            ...strategies,
            [httpErrorCode]: () => {
                return true;
            },
        }),
        {}
    );

    const errorCallback = errorCallbackFactory.createCallback({
        strategies,
        logError: false,
    });

    return withRequestRetry(request, undefined, errorCallback, retryOptions);
}
